/**
 * Per-route SEO metadata — the single source used both at build time (prerendered
 * HTML, sitemap) and in the browser on client-side navigation (usePageMeta).
 */

export const SITE_NAME = 'Grow My Restaurant';
export const DEFAULT_OG_IMAGE = '/og-image.png';

export interface PageMeta {
  title: string;
  description: string;
  /** Path used for the canonical URL (defaults to the page path). */
  canonicalPath?: string;
  /** false → <meta name="robots" content="noindex, follow"> */
  index: boolean;
  /** Include in sitemap.xml. */
  sitemap?: { changefreq: 'daily' | 'weekly' | 'monthly'; priority: number };
  ogImage?: string;
  jsonLd?: (siteUrl: string) => Record<string, unknown>[];
}

const FAQ = [
  {
    q: 'Is the restaurant website grader really free?',
    a: 'Yes. Grading your website is free and you don’t need an account. Create one only if you want to save reports and track your score over time.',
  },
  {
    q: 'What does the grader check?',
    a: 'More than 80 checks across Google search basics, local SEO, your Google Business Profile, page speed, mobile experience and restaurant essentials like your menu, online ordering and reservations.',
  },
  {
    q: 'How long does a scan take?',
    a: 'Usually under two minutes. We scan up to 100 pages of your website and run Google’s page-speed test while you wait.',
  },
  {
    q: 'Why is my menu format important for SEO?',
    a: 'Google can read menus written as text on a web page, so you can rank for dish searches like “best butter chicken near me”. PDF and image menus are much harder for search engines and phones.',
  },
  {
    q: 'How can I improve my restaurant’s Google ranking?',
    a: 'Start with the critical issues and quick wins in your report: put your city and cuisine in page titles and headings, list your address, phone and hours as text, keep your Google Business Profile complete, and make your site fast on phones.',
  },
] as const;

export const LANDING_FAQ = FAQ;

const organization = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: siteUrl,
  logo: `${siteUrl}/favicon.svg`,
});

const website = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: siteUrl,
});

const webApplication = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: `${SITE_NAME} — Restaurant Website Grader`,
  url: `${siteUrl}/grade`,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Any',
  description:
    'Free tool that grades restaurant websites on SEO, local search, page speed, mobile and restaurant essentials, with plain-English fixes.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
});

const faqPage = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

const NOINDEX = { index: false } as const;

export const ROUTE_META: Record<string, PageMeta> = {
  '/': {
    title: `Free Restaurant Website Grader & SEO Check | ${SITE_NAME}`,
    description:
      'Get a free score for your restaurant website in 2 minutes. Check Google visibility, local SEO, menu, online ordering and page speed — with plain-English fixes.',
    index: true,
    sitemap: { changefreq: 'weekly', priority: 1.0 },
    jsonLd: (u) => [organization(u), website(u), webApplication(u), faqPage()],
  },
  '/grade': {
    title: `Grade Your Restaurant Website — Free SEO Report | ${SITE_NAME}`,
    description:
      'Enter your restaurant’s name and website to get a free SEO and website health report with prioritised fixes for Google, local search and online ordering.',
    index: true,
    sitemap: { changefreq: 'monthly', priority: 0.9 },
    jsonLd: (u) => [webApplication(u)],
  },
  '/restaurant/select': {
    title: `Find Your Restaurant | ${SITE_NAME}`,
    description: 'Find your restaurant and confirm its website to get a free website grade.',
    canonicalPath: '/grade',
    ...NOINDEX,
  },
  '/login': {
    title: `Sign In | ${SITE_NAME}`,
    description: 'Sign in to see your saved restaurant website reports.',
    ...NOINDEX,
  },
  '/register': {
    title: `Create Your Account | ${SITE_NAME}`,
    description: 'Create a free account to save reports and track your restaurant website score.',
    ...NOINDEX,
  },
  '/dashboard': {
    title: `Your Reports | ${SITE_NAME}`,
    description: 'Your saved restaurant website reports.',
    ...NOINDEX,
  },
  '/scan': {
    title: `Grading Your Website… | ${SITE_NAME}`,
    description: 'Your restaurant website is being graded.',
    ...NOINDEX,
  },
  '/report': {
    title: `Website Report | ${SITE_NAME}`,
    description: 'Restaurant website report.',
    ...NOINDEX,
  },
  '/404': {
    title: `Page Not Found | ${SITE_NAME}`,
    description: 'This page isn’t on the menu.',
    ...NOINDEX,
  },
};

/** Meta for a concrete path (dynamic routes like /report/:id fall back to their prefix). */
export function metaForPath(pathname: string): PageMeta {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (ROUTE_META[clean]) return ROUTE_META[clean];
  const prefix = Object.keys(ROUTE_META).find((k) => k !== '/' && clean.startsWith(`${k}/`));
  return ROUTE_META[prefix ?? '/404']!;
}

/** Paths listed in sitemap.xml. */
export const SITEMAP_PATHS = Object.entries(ROUTE_META)
  .filter(([, m]) => m.index && m.sitemap)
  .map(([path, m]) => ({ path, ...m.sitemap! }));

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** <head> tags for a page, used by the prerender step. */
export function renderHeadTags(path: string, meta: PageMeta, siteUrl: string): string {
  const url = `${siteUrl}${meta.canonicalPath ?? path}`;
  const image = `${siteUrl}${meta.ogImage ?? DEFAULT_OG_IMAGE}`;
  const tags = [
    `<title>${esc(meta.title)}</title>`,
    `<meta name="description" content="${esc(meta.description)}" />`,
    `<meta name="robots" content="${meta.index ? 'index, follow, max-image-preview:large' : 'noindex, follow'}" />`,
    // Noindex pages don't declare a canonical (it would point crawlers at an unindexed URL).
    ...(meta.index || meta.canonicalPath ? [`<link rel="canonical" href="${esc(url)}" />`] : []),
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:title" content="${esc(meta.title)}" />`,
    `<meta property="og:description" content="${esc(meta.description)}" />`,
    `<meta property="og:url" content="${esc(url)}" />`,
    `<meta property="og:image" content="${esc(image)}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="${SITE_NAME} — free restaurant website grader" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${esc(meta.title)}" />`,
    `<meta name="twitter:description" content="${esc(meta.description)}" />`,
    `<meta name="twitter:image" content="${esc(image)}" />`,
  ];
  for (const block of meta.jsonLd?.(siteUrl) ?? []) {
    // `<` escaped so content can never close the script element.
    tags.push(
      `<script type="application/ld+json">${JSON.stringify(block).replace(/</g, '\\u003c')}</script>`,
    );
  }
  return tags.join('\n    ');
}
